import React from 'react';
import { Box, RadioCard } from '@indeed/ifl-components';
import {
    DateQuestion,
    FileUploadQuestion,
    HierarchicalSelectQuestion,
    MultiSelectQuestion,
    NumberQuestion,
    QuestionField,
    RichTextQuestion,
    SingleSelectQuestion,
    TextQuestion,
    createSelectQuestionValidateFn,
    useFieldTelemetryEvents,
} from '@indeed/jspl-questions';
import { useFieldValidation } from '@indeed/jspl-validation';
import GettextFactory from '../../../util/GettextFactory';
import type { ScreenerQuestionContextQuestion } from '../../../__generated__/graphql-base-types';
import { dateBoundToISO } from './screenerQuestionUtils';
import type { AnswerValue, ScreenerFileAnswer } from './screenerQuestionUtils';

export interface ScreenerQuestionFieldProps {
    question: ScreenerQuestionContextQuestion;
    /**
     * Current answer. FILE questions carry `ScreenerFileAnswer[]`; every other type keeps the flat
     * `string[]` (see `AnswerValue`).
     */
    value: AnswerValue;
    onChange: (value: AnswerValue) => void;
    /** FILE only: upload a newly selected file — the page owns the optimistic add and async replace. */
    onFileSelect?: (file: File) => void;
    /** FILE only: remove the file at the given index. */
    onFileRemove?: (index: number) => void;
}

/**
 * Smart Apply switches SELECT from radios to a dropdown above this many options.
 * jspl's own auto-resolve threshold is 5, so the variant is always passed explicitly.
 */
const DROPDOWN_CUTOFF_THRESHOLD = 6;

const NUMERIC_FORMATS: Partial<
    Record<
        NonNullable<ScreenerQuestionContextQuestion['format']>,
        'integer' | 'decimal' | 'numericText'
    >
> = {
    INTEGER: 'integer',
    DECIMAL: 'decimal',
    CURRENCY: 'decimal',
    NUMERIC_TEXT: 'numericText',
};

interface SharedQuestionProps {
    label: string;
    name: string;
    showRequiredIndicator: boolean;
    includeVisuallyHiddenRequired: boolean;
}

interface RadioCardSelectFieldProps {
    question: ScreenerQuestionContextQuestion;
    shared: SharedQuestionProps;
    /** Selected option value, or '' when unanswered. */
    value: string;
    onChange: (value: string[]) => void;
}

/**
 * SELECT rendered as radio cards, registered with the surrounding `FieldValidationProvider`.
 *
 * `QuestionField` is only a presentational shell — unlike `SingleSelectQuestion` it registers
 * nothing, so without this the group would be invisible to jspl: the form would read as valid with
 * the question unanswered, and `exposeErrors()` would have no error to show. Registration mirrors
 * `SingleSelectQuestion`'s radio variant so the group validates, focuses and reports the same way.
 *
 * Also reports field telemetry itself, for the same reason: `QuestionField` doesn't call
 * `useFieldTelemetryEvents` either, so without this the question would surface to
 * `useQuestionInteractionTelemetry` as permanently unviewed/unanswered. Wired to match
 * `SingleSelectQuestion`'s radio variant — `onFocus` per option, `onAnswer` (no separate `onChange`
 * dispatch) once one is picked, since ANSWERED already marks the field dirty/touched.
 */
const RadioCardSelectField: React.FunctionComponent<RadioCardSelectFieldProps> = ({
    question,
    shared,
    value,
    onChange,
}) => {
    const gt = GettextFactory();
    const { name, required, options } = question;
    const errorTextId = `screener-question-error-${React.useId()}`;
    const { fields, register } = useFieldValidation();
    const field = fields[name];
    // A state-backed callback ref (not a plain `useRef`) so mounting the cards triggers a
    // re-render, letting the telemetry hook start observing the real container on the next render
    // instead of waiting on some unrelated one to eventually pick up a fresh `ref.current`.
    const [radioCardsElement, setRadioCardsElement] = React.useState<HTMLDivElement | null>(null);
    const telemetry = useFieldTelemetryEvents(name, {
        visibilityElement: radioCardsElement ?? undefined,
    });

    const validate = React.useMemo(
        () =>
            createSelectQuestionValidateFn(
                name,
                { required: gt.gettext('Choose an option to continue.') },
                { required },
            ),
        [name, required, gt],
    );
    const registration = register(name, { value, validate, debounce: 0 });

    React.useEffect(() => {
        telemetry.onValidate({
            errorMessage: field?.errorText ?? '',
            errorVisible: !!field?.errorVisible,
        });
    }, [telemetry, field?.errorText, field?.errorVisible]);

    return (
        <QuestionField
            {...shared}
            as="fieldset"
            invalid={field?.errorVisible}
            errorText={field?.errorText}
            errorTextId={errorTextId}
        >
            <Box
                ref={setRadioCardsElement}
                sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
            >
                {options.map((opt) => (
                    // `label` puts the option text inside the radio's own <label>, which both
                    // names the input and lays it out beside the control. Passing no children
                    // keeps these as short cards with no body padding.
                    <RadioCard
                        key={opt.value}
                        name={name}
                        label={opt.label}
                        value={opt.value}
                        checked={value === opt.value}
                        invalid={field?.errorVisible}
                        aria-describedby={errorTextId}
                        onChange={() => {
                            registration.onChange(opt.value);
                            onChange([opt.value]);
                            telemetry.onAnswer({ answerLength: 1 });
                        }}
                        onFocus={telemetry.onFocus}
                        onBlur={registration.onBlur}
                    />
                ))}
            </Box>
        </QuestionField>
    );
};

export const ScreenerQuestionField: React.FunctionComponent<ScreenerQuestionFieldProps> = ({
    question,
    value,
    onChange,
    onFileSelect,
    onFileRemove,
}) => {
    const { name, required, options } = question;
    // Every type except FILE carries `string[]`; FILE narrows to `ScreenerFileAnswer[]` in its case.
    const stringValue = value as string[];
    const single = stringValue[0] ?? '';

    // `includeVisuallyHiddenRequired` (jspl-questions 5.6+) keeps `aria-required` off the label
    // element — invalid ARIA there — and puts a visually hidden "required" in the accessible name
    // instead. That lets us pass `showRequiredIndicator`, which is also what jspl's built-in
    // validators read to enforce `required`.
    const shared = {
        label: question.labelHtml,
        name,
        showRequiredIndicator: required,
        includeVisuallyHiddenRequired: required,
    };

    switch (question.type) {
        case 'SELECT':
            // Above the cutoff Smart Apply falls back to a dropdown; at or below it the design
            // calls for radio cards rather than the bare radios SingleSelectQuestion renders.
            if (options.length > DROPDOWN_CUTOFF_THRESHOLD) {
                return (
                    <SingleSelectQuestion
                        {...shared}
                        items={options}
                        variant="select"
                        value={single}
                        onChange={(v) => onChange(v ? [v] : [])}
                    />
                );
            }

            return (
                <RadioCardSelectField
                    question={question}
                    shared={shared}
                    value={single}
                    onChange={onChange}
                />
            );

        case 'MULTISELECT':
            return (
                <MultiSelectQuestion
                    {...shared}
                    items={options}
                    value={stringValue}
                    minSelections={question.minSelectionCount ?? undefined}
                    maxSelections={question.maxSelectionCount ?? undefined}
                    onChange={(v) => onChange(v ?? [])}
                />
            );

        case 'HIERARCHICAL':
            return (
                <HierarchicalSelectQuestion
                    {...shared}
                    question={question}
                    value={stringValue}
                    onChange={onChange}
                />
            );

        case 'DATE': {
            // `value` stays in `outputDatePattern` — that is the format DateQuestion reads and
            // writes — but its `min`/`max` bounds are ISO 8601 (see dateBoundToISO).
            const { outputDatePattern } = question;

            return (
                <DateQuestion
                    {...shared}
                    value={single}
                    min={dateBoundToISO(question.min, outputDatePattern)}
                    max={dateBoundToISO(question.max, outputDatePattern)}
                    dateFormat={outputDatePattern || undefined}
                    // Pin the field's invalid flag to jspl's validation state. Without an explicit
                    // value the underlying IFL Input sees `invalid === undefined` and falls back to
                    // native `checkValidity()`, which only refreshes on blur — so a required date
                    // filled via the picker (which never blurs the input) stays red despite being
                    // valid. `false` here is still ORed with jspl's `errorVisible`, so genuine
                    // errors continue to show.
                    inputProps={{ invalid: false }}
                    onChange={(v) => onChange(v ? [v] : [])}
                />
            );
        }

        case 'FILE': {
            // Uploads are owned by the page (it holds the draft/job context and does the optimistic
            // add + async replace); this field just shows each file and its in-flight state.
            const files = value as ScreenerFileAnswer[];
            return (
                <FileUploadQuestion
                    {...shared}
                    documents={files.map((file) => ({
                        name: file.name,
                        uploading: file.uploading,
                    }))}
                    accept={question.fileFormats.map((ext) => `.${ext}`).join(',') || undefined}
                    maxDocuments={question.max ? parseInt(question.max, 10) : undefined}
                    onSelect={(file) => onFileSelect?.(file)}
                    onRemove={(index) => onFileRemove?.(index)}
                />
            );
        }

        case 'TEXTAREA':
            // RichTextQuestion is jspl's textarea question — it wraps IFL TextArea and registers
            // with the validation provider, which a bare QuestionField + TextArea does not.
            return (
                <RichTextQuestion
                    {...shared}
                    value={single}
                    maxLength={question.limit ?? undefined}
                    minLength={question.minCharCount ?? undefined}
                    onChange={(v) => onChange(v ? [v] : [])}
                />
            );

        case 'TEXT':
        default: {
            const numericFormat = question.format ? NUMERIC_FORMATS[question.format] : undefined;

            if (numericFormat) {
                return (
                    <NumberQuestion
                        {...shared}
                        format={numericFormat}
                        value={single}
                        min={question.min ?? undefined}
                        max={question.max ?? undefined}
                        maxLength={question.limit ?? undefined}
                        onChange={(v) => onChange(v ? [v] : [])}
                    />
                );
            }

            return (
                <TextQuestion
                    {...shared}
                    value={single}
                    maxLength={question.limit ?? undefined}
                    minLength={question.minCharCount ?? undefined}
                    onChange={(v) => onChange(v ? [v] : [])}
                />
            );
        }
    }
};
